import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Instagram } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { FadeUp } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { usePageTitle } from "../hooks/usePageTitle";

const TYPES = ["GENERAL INQUIRY", "ORIGINAL ART", "PRINT QUESTION", "MURAL COMMISSION", "EVENT / EXHIBITION OPPORTUNITY"];

const inputCls = "w-full h-12 bg-charcoal border border-white/15 px-4 text-sm outline-none focus:border-pink placeholder:text-smoke/50";
const labelCls = "block text-[10px] font-bold tracking-[0.25em] text-smoke mb-2";

export default function Contact({ config }) {
  usePageTitle("Contact the Artist");
  const [params] = useSearchParams();
  const initialType = TYPES.includes(params.get("type")) ? params.get("type") : "GENERAL INQUIRY";
  const piece = params.get("piece") || "";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", inquiry_type: initialType,
    message: piece ? `Regarding: ${piece}\n\n` : "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries/contact", form);
      toast.success("Inquiry sent — Kenny will be in touch.");
      setForm({ name: "", email: "", phone: "", inquiry_type: initialType, message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pb-20" data-testid="contact-page">
      <PageHero slug="piece-14" testId="contact-hero">
        <FadeUp>
          <p className="text-[10px] font-bold tracking-[0.3em] text-smoke mb-3">GET IN TOUCH</p>
          <h1 className="font-display text-6xl sm:text-7xl tracking-wide leading-none" data-testid="contact-title">CONTACT</h1>
        </FadeUp>
      </PageHero>

      <div className="mt-10 px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12 max-w-4xl">
        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
          <div>
            <label htmlFor="c-type" className={labelCls}>INQUIRY TYPE *</label>
            <select id="c-type" value={form.inquiry_type} onChange={set("inquiry_type")} className={inputCls} data-testid="contact-type">
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            {form.inquiry_type === "MURAL COMMISSION" && (
              <p className="mt-2 text-xs text-smoke">
                For murals, the <Link to="/murals" className="text-pink underline underline-offset-4" data-testid="contact-mural-link">mural project form</Link> captures wall details and photos.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><label htmlFor="c-name" className={labelCls}>NAME *</label>
              <input id="c-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" /></div>
            <div><label htmlFor="c-email" className={labelCls}>EMAIL *</label>
              <input id="c-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" /></div>
          </div>
          <div><label htmlFor="c-phone" className={labelCls}>PHONE (OPTIONAL)</label>
            <input id="c-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone" /></div>
          <div><label htmlFor="c-message" className={labelCls}>MESSAGE *</label>
            <textarea id="c-message" required rows={5} value={form.message} onChange={set("message")}
              className="w-full bg-charcoal border border-white/15 px-4 py-3 text-sm outline-none focus:border-pink" data-testid="contact-message" /></div>
          <button type="submit" disabled={sending} data-testid="contact-submit"
            className="w-full sm:w-auto h-12 px-10 bg-bone text-ink text-sm font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200 disabled:opacity-50">
            {sending ? "SENDING…" : "SEND INQUIRY"}
          </button>
        </form>

        <FadeUp delay={0.15}>
          <div className="border border-white/10 p-5">
            <p className="text-[10px] font-bold tracking-[0.25em] text-smoke">ELSEWHERE</p>
            <a href={config?.instagram || "https://instagram.com/swingkidd"} target="_blank" rel="noopener noreferrer"
              data-testid="contact-instagram-link"
              className="mt-4 flex items-center gap-3 text-sm font-bold hover:text-pink transition-colors duration-200 min-h-[44px]">
              <Instagram size={18} className="text-pink" /> @swingkidd
            </a>
            <p className="mt-4 text-xs text-smoke leading-relaxed">
              Original artwork, editions and mural commissions — every inquiry goes directly to the artist.
            </p>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
